import React, { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Area, ComposedChart, Scatter } from 'recharts';
import {
  calculateFingerDurationDelta,
  calculateHandDurationDelta,
  calculateAverage,
  calculateMedian,
  calculateStdDev,
  calculateInRangePercentage,
  calculateLinearRegression,
  calculateDirectionDurationDelta
} from '../../utils/articulationUtils';

// Helper to calculate standard deviation from array of values
const calcStdDevFromValues = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Helper to get Y domain for duration delta (bidirectional)
const getDurationYDomain = () => [-20, 20];

// Helper to generate Y-axis ticks for duration delta
const generateDurationTicks = () => [-20, -10, 0, 10, 20];

const ArticulationBreakout = ({ sessions, selection, onClose }) => {
  const { entity, exercise, exerciseType } = selection;

  // Filter sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      if (exercise) {
        return s.exercise_name === exercise;
      } else if (exerciseType) {
        return s.exercise_type === exerciseType;
      }
      return true;
    });
  }, [sessions, exercise, exerciseType]);

  // Determine if it's a hand or finger
  const isHand = entity.includes('_hand');
  const hand = entity.split('_')[0];
  const finger = isHand ? null : parseInt(entity.split('_')[2]);

  // Build field name
  const field = isHand 
    ? `${hand}_relative_duration_avg`
    : `${hand}_finger_${finger}_relative_duration_avg`;

  // Calculate statistics
  const avgDelta = calculateAverage(filteredSessions, field);
  const medianDelta = calculateMedian(filteredSessions, field);
  const stdDevDelta = calculateStdDev(filteredSessions, field);
  const inRangePercent = calculateInRangePercentage(filteredSessions, field, 5);

  // Calculate direction deltas (up vs down)
  const upDelta = calculateDirectionDurationDelta(filteredSessions, hand, finger, 'up');
  const downDelta = calculateDirectionDurationDelta(filteredSessions, hand, finger, 'down');

  // Calculate parent averages
  const parentHandField = isHand ? null : `${hand}_relative_duration_avg`;
  const parentHandAvg = !isHand ? calculateAverage(filteredSessions, parentHandField) : null;

  // Calculate parent type average (if exercise is selected)
  const parentTypeAvg = exercise
    ? calculateAverage(
        sessions.filter(s => s.exercise_type === exerciseType),
        field
      )
    : null;

  // Prepare time series data
  const timeSeriesData = useMemo(() => {
    return filteredSessions
      .filter(s => s[field] != null)
      .map(s => ({
        timestamp: new Date(s.created_at).getTime(),
        date: new Date(s.created_at).toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric',
          minute: '2-digit',
          hour12: true 
        }),
        value: s[field]
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [filteredSessions, field]);

  // Calculate regression for time series with std dev band
  const timeRegressionData = useMemo(() => {
    if (timeSeriesData.length < 2) return [];
    
    const points = timeSeriesData.map(d => ({ x: d.timestamp, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = timeSeriesData.map(d => d.value);
    const stdDev = calcStdDevFromValues(values);
    
    const minX = Math.min(...timeSeriesData.map(d => d.timestamp));
    const maxX = Math.max(...timeSeriesData.map(d => d.timestamp));
    
    const regressionPoints = [];
    const step = (maxX - minX) / 20;
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        timestamp: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [timeSeriesData]);

  // Prepare tempo scatter data
  const tempoScatterData = useMemo(() => {
    return filteredSessions
      .filter(s => s.calculated_tempo != null && s[field] != null)
      .map(s => ({
        tempo: s.calculated_tempo,
        value: s[field]
      }));
  }, [filteredSessions, field]);

  // Prepare tempo regression data
  const tempoRegressionData = useMemo(() => {
    if (tempoScatterData.length < 2) return [];
    
    const points = tempoScatterData.map(d => ({ x: d.tempo, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = tempoScatterData.map(d => d.value);
    const stdDev = calcStdDevFromValues(values);
    
    const minX = Math.min(...tempoScatterData.map(d => d.tempo));
    const maxX = Math.max(...tempoScatterData.map(d => d.tempo));
    
    const regressionPoints = [];
    const step = (maxX - minX) / 20;
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        tempo: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [tempoScatterData]);

  // Prepare repetition scatter data
  const repetitionScatterData = useMemo(() => {
    return filteredSessions
      .filter(s => s[field] != null)
      .map((s, idx) => ({
        repetition: filteredSessions.length - idx,
        value: s[field]
      }))
      .reverse();
  }, [filteredSessions, field]);

  // Prepare repetition regression data
  const repetitionRegressionData = useMemo(() => {
    if (repetitionScatterData.length < 2) return [];
    
    const points = repetitionScatterData.map(d => ({ x: d.repetition, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = points.map(p => p.y);
    const stdDev = calcStdDevFromValues(values);
    
    const minX = 1;
    const maxX = repetitionScatterData.length;
    
    const regressionPoints = [];
    const step = Math.max(1, (maxX - minX) / 20);
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        repetition: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [repetitionScatterData]);

  // Determine color based on entity (left = purple, right = orange)
  const color = entity.includes('left') ? '#9333ea' : '#ea580c';

  // Helper to format exercise names
  const formatExerciseName = (name) => {
    if (!name) return 'All';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format entity name for display
  const entityLabel = isHand
    ? `${hand === 'left' ? 'Left' : 'Right'} Hand`
    : `${hand === 'left' ? 'Left' : 'Right'} Finger ${finger}`;

  const exerciseLabel = formatExerciseName(exercise) || (exerciseType ? formatExerciseName(exerciseType) + 's' : 'All');

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-start">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{entityLabel}</h2>
          <p className="text-sm text-gray-600">{exerciseLabel}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          aria-label="Close detailed stats"
        >
          <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Content - Horizontal Layout */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-4 gap-4">
          {/* Statistics Panel */}
          <div className="bg-gray-50 p-4 rounded-lg flex flex-col" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Duration Delta Stats</h3>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs flex-1">
              <div>
                <div className="text-gray-600">Average Δ</div>
                <div className="font-semibold text-gray-900">{avgDelta?.toFixed(1) || 'N/A'}%</div>
              </div>
              <div>
                <div className="text-gray-600">Median Δ</div>
                <div className="font-semibold text-gray-900">{medianDelta?.toFixed(1) || 'N/A'}%</div>
              </div>
              <div>
                <div className="text-gray-600">Std Dev</div>
                <div className="font-semibold text-gray-900">{stdDevDelta?.toFixed(2) || 'N/A'}</div>
              </div>
              <div>
                <div className="text-gray-600">±5% Range</div>
                <div className="font-semibold text-gray-900">{inRangePercent?.toFixed(1) || 'N/A'}%</div>
              </div>
              <div>
                <div className="text-gray-600">Up Direction Δ</div>
                <div className="font-semibold text-gray-900">{upDelta?.toFixed(1) || 'N/A'}%</div>
              </div>
              <div>
                <div className="text-gray-600">Down Direction Δ</div>
                <div className="font-semibold text-gray-900">{downDelta?.toFixed(1) || 'N/A'}%</div>
              </div>
              {(parentHandAvg !== null || parentTypeAvg !== null) && (
                <>
                  <div className="col-span-2 pt-2 border-t border-gray-300"></div>
                  {parentHandAvg !== null && (
                    <div>
                      <div className="text-gray-600">{hand === 'left' ? 'Left' : 'Right'} Hand Δ</div>
                      <div className="font-semibold text-gray-900">{parentHandAvg.toFixed(1)}%</div>
                    </div>
                  )}
                  {parentTypeAvg !== null && (
                    <div>
                      <div className="text-gray-600">{exerciseType ? exerciseType.charAt(0).toUpperCase() + exerciseType.slice(1) + 's' : 'All'} Δ</div>
                      <div className="font-semibold text-gray-900">{parentTypeAvg.toFixed(1)}%</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Performance over Time */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Over Time</h3>
            {timeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-breakout-time-art" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="timestamp"
                    name="Date"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(ts) => {
                      const date = new Date(ts);
                      return date.toLocaleString(undefined, { 
                        month: 'short', 
                        day: 'numeric'
                      });
                    }}
                  />
                  <YAxis 
                    type="number"
                    domain={getDurationYDomain()}
                    ticks={generateDurationTicks()}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const date = new Date(data.timestamp);
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs font-semibold">{date.toLocaleDateString()}</p>
                            <p className="text-xs">Duration Δ: {data.value?.toFixed(1)}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter
                    data={timeSeriesData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {timeRegressionData.length > 0 && (
                    <>
                      <Area
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-breakout-time-art)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>

          {/* Performance by Tempo */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Tempo</h3>
            {tempoScatterData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-breakout-tempo-art" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number"
                    dataKey="tempo"
                    name="Tempo"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(value) => Math.round(value)}
                  />
                  <YAxis 
                    type="number"
                    domain={getDurationYDomain()}
                    ticks={generateDurationTicks()}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs">Tempo: {data.tempo ? Math.round(data.tempo) : 'N/A'} BPM</p>
                            <p className="text-xs">Duration Δ: {data.value?.toFixed(1)}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter
                    data={tempoScatterData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {tempoRegressionData.length > 0 && (
                    <>
                      <Area
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-breakout-tempo-art)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>

          {/* Performance by Repetition */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Repetition</h3>
            {repetitionScatterData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-breakout-rep-art" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number"
                    dataKey="repetition"
                    name="Repetition"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(value) => Math.round(value)}
                  />
                  <YAxis 
                    type="number"
                    domain={getDurationYDomain()}
                    ticks={generateDurationTicks()}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs">Rep: #{Math.round(data.repetition)}</p>
                            <p className="text-xs">Duration Δ: {data.value?.toFixed(1)}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter
                    data={repetitionScatterData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {repetitionRegressionData.length > 0 && (
                    <>
                      <Area
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-breakout-rep-art)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticulationBreakout;

